import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TogglePlan from './TogglePlan';
import PlanCard from './PlanCard';
import PaymentSystemPanel from './PaymentSystemPanel';
import { PLAN_CATALOG, PLAN_DEFINITIONS, normalizePlanName } from './planCatalog';

const CompactPlan = ({ plan, apiPlan, billingSchedule }) => {
  const navigate = useNavigate();
  const isAnnual = billingSchedule === 'year';
  const price = isAnnual ? plan.annualMonthlyPrice : plan.monthlyPrice;
  const isSalesLed = plan.group === 'high-volume';
  const apiPrice = apiPlan?.prices?.find(
    (priceOption) => priceOption.schedule === billingSchedule
  );

  const handleCTA = () => {
    if (isSalesLed) {
      navigate('/contact/get-in-touch');
      return;
    }
    navigate('/signup', { state: { pr_id: apiPrice?.pr_id } });
  };

  return (
    <article className="group rounded-[16px] bg-white/[0.055] p-5 ring-1 ring-inset ring-white/10 transition-colors hover:bg-white/[0.09] max_600:min-w-[82%] max_600:snap-center">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-[19px] font-DMSansFont font-bold text-white">{plan.name}</h3>
          <p className="mt-1 text-[12px] font-semibold text-[#75C9FF]">{plan.outcome}</p>
        </div>
        <div className="shrink-0 text-right">
          <span className="text-[23px] font-DMSansFont font-bold text-white">
            ${price.toLocaleString()}
          </span>
          {price > 0 && <span className="text-[11px] text-white/40">/mo</span>}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-white/55">
        <span><strong className="text-white">{plan.users}</strong> users</span>
        <span><strong className="text-white">{plan.records}</strong> records</span>
        <span><strong className="text-white">{plan.concurrency}</strong> concurrency</span>
      </div>

      <button
        type="button"
        onClick={handleCTA}
        className="mt-4 w-full rounded-[9px] bg-white/10 px-4 py-2.5 text-[12px] font-bold text-white transition-colors group-hover:bg-white group-hover:text-LightPrimary"
      >
        {plan.cta}
      </button>
    </article>
  );
};

const Plans = ({ allPlansData }) => {
  const pricingOptions = [
    { id: 1, name: 'Monthly', val: 'month' },
    { id: 2, name: 'Annual — Save 20%', val: 'year' },
  ];
  const [selected, setSelected] = useState(pricingOptions[0]);
  const apiPlans = useMemo(
    () => new Map((allPlansData || []).map((plan) => [normalizePlanName(plan.name), plan])),
    [allPlansData]
  );
  const featuredPlans = PLAN_CATALOG.filter((plan) => ['Startup', 'Grow', 'Pro'].includes(plan.name));
  const otherPlans = PLAN_CATALOG.filter((plan) => !['Startup', 'Grow', 'Pro'].includes(plan.name));
  const onDemandPrId = apiPlans
    .get('on demand')
    ?.prices?.find((priceOption) => priceOption.schedule === 'month')?.pr_id;

  return (
    <section className="mx-auto mt-8 max-w-[1240px] px-10 max_sm:mt-5 max_sm:px-5">
      <div className="relative overflow-hidden rounded-[30px] bg-LightPrimary px-8 py-9 shadow-[0_28px_80px_rgba(25,34,76,0.18)] max_sm:rounded-[22px] max_sm:px-4 max_sm:py-6">
        <div className="pointer-events-none absolute -right-40 -top-48 h-[420px] w-[420px] rounded-full bg-ButtonPrimary/20 blur-[90px]" />
        <div className="relative flex items-center justify-between gap-6 max_600:flex-col max_600:items-start">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#75C9FF]">Choose your capacity</p>
            <h2 className="mt-2 text-[28px] font-DMSansFont font-bold text-white max_sm:text-[24px]">
              Start with the plan built for your next stage
            </h2>
          </div>
          <TogglePlan selected={selected} setSelected={setSelected} pricingOptions={pricingOptions} />
        </div>

        <div className="relative mt-8 grid grid-cols-3 gap-5 max_md:grid-cols-2 max_600:flex max_600:snap-x max_600:overflow-x-auto max_600:pb-3">
          {featuredPlans.map((plan) => (
            <PlanCard
              key={plan.name}
              plan={plan}
              apiPlan={apiPlans.get(normalizePlanName(plan.name))}
              billingSchedule={selected.val}
            />
          ))}
        </div>

        <div className="relative mt-8 border-t border-white/10 pt-7">
          <div className="flex items-end justify-between gap-4 max_600:block">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#75C9FF]">Every stage covered</p>
              <h2 className="mt-1 text-[22px] font-DMSansFont font-bold text-white">More ways to start and scale</h2>
            </div>
            <p className="text-[12px] text-white/50 max_600:mt-2">Entry options through enterprise capacity.</p>
          </div>
          <div className="mt-5 grid grid-cols-3 gap-3 max_md:grid-cols-2 max_600:flex max_600:snap-x max_600:overflow-x-auto max_600:pb-3">
            {otherPlans.map((plan) => (
              <CompactPlan
                key={plan.name}
                plan={plan}
                apiPlan={apiPlans.get(normalizePlanName(plan.name))}
                billingSchedule={selected.val}
              />
            ))}
          </div>
        </div>

        <div id="payments" className="relative mt-9 border-t border-white/10 pt-8">
          <div className="flex items-end justify-between gap-4 max_600:block">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#75C9FF]">Payment system</p>
              <h2 className="mt-2 text-[24px] font-DMSansFont font-bold text-white max_sm:text-[22px]">
                Collect payments — with or without a paid plan
              </h2>
            </div>
            <p className="max-w-[340px] text-[12px] leading-5 text-white/50 max_600:mt-2 max_600:max-w-none">
              Every DCDial account runs on a software plan. Only collecting payments? The free
              On Demand plan is all you need.
            </p>
          </div>
          <PaymentSystemPanel onDemandPrId={onDemandPrId} />
        </div>
      </div>

      <details id="plan-limit-definitions" className="mt-5 rounded-[16px] bg-[#F4F7FB]">
        <summary className="cursor-pointer px-5 py-4 text-[14px] font-bold text-LightPrimary">
          What do the plan limits mean?
        </summary>
        <div className="grid grid-cols-5 gap-4 border-t border-[#E2E8F0] p-5 max_md:grid-cols-2 max_600:grid-cols-1">
          {PLAN_DEFINITIONS.map((item) => (
            <div key={item.term}>
              <h3 className="text-[13px] font-bold text-LightPrimary">{item.term}</h3>
              <p className="mt-1 text-[12px] leading-5 text-LightTextParagraph">{item.definition}</p>
            </div>
          ))}
        </div>
      </details>
    </section>
  );
};

export default Plans;
